// components/QCard.tsx — V28 blueprint C6.
// Maps to the prototype `.qcard` (v28-prototype/index.html 914-953; markup in
// screens/ripasso.html 27-53 + simula-exam.html 42-67).
//
// A STANDALONE question card (white surface, hairline border, soft card shadow) —
// NOT a Prof-message bubble. Structure:
//   ┌──────────────────────────────┐
//   │  .media  (~140px lavender,    │  ← real sign <img> OR an honest dashed
//   │           45° hatched slot)   │     placeholder when no image is available
//   ├──────────────────────────────┤
//   │  .qbody → .qtext + children   │  ← statement text + e.g. <VeroFalsoPills/>
//   └──────────────────────────────┘
//
// The media slot supports two HONEST modes (never a fake image):
//   - `imageRef` set        → real sign PNG served from Vite public (same path
//                             form as QuestionCard/InlineImage: `/${imageRef}`).
//   - no image (or onError) → a dashed `.imgslot`-style placeholder with an icon
//                             + short Italian caption, so the absence is explicit.
// When `medialess` is true the whole media slot is omitted (text-only V/F items,
// e.g. most quiz statements that carry no diagram).
//
// Light-only: every value comes from `useTokens()` (tokens.mode resolves 'light').
// The 45° violet hatch matches the prototype literal (brand violet at 4% alpha) —
// there is no discrete hatch token, so it stays a literal here as in the prototype.

import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { useTokens } from '../state/theme';

export interface QCardProps {
  /** The question / statement text shown in `.qtext`. */
  text: string;
  /**
   * Optional real sign image — relative path from the backend, e.g.
   * "img_sign_by_hash/HASH.png" or "img_sign/N.png" (same form QuestionCard uses).
   * Rendered as `/${imageRef}`. Falls back to the dashed placeholder on load error.
   */
  imageRef?: string | null;
  /** Alt text for the real sign image (Italian). Defaults to a generic fallback. */
  imageAlt?: string;
  /**
   * Caption shown inside the honest dashed placeholder when there is no image.
   * Italian. Defaults to "Immagine non disponibile".
   */
  placeholderCaption?: string;
  /**
   * When true, omit the media slot entirely (text-only statement). Default false →
   * the media slot is always present (image or honest placeholder).
   */
  medialess?: boolean;
  /** Body content below the question text — typically `<VeroFalsoPills/>`. */
  children?: ReactNode;
}

/** Mountain/landscape glyph — the prototype's "image" affordance for the slot. */
function MediaGlyph({ size, color }: { size: number; color: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

export function QCard({
  text,
  imageRef,
  imageAlt = 'Segnale stradale',
  placeholderCaption = 'Immagine non disponibile',
  medialess = false,
  children,
}: QCardProps): JSX.Element {
  const t = useTokens();
  const [broken, setBroken] = useState(false);

  const showRealImage = !medialess && !!imageRef && !broken;
  const showPlaceholder = !medialess && (!imageRef || broken);

  // 45° lavender hatch over surface2 — exact translation of the prototype `.media`
  // background. The 4%-alpha violet is brand-ink (#6943ff); no discrete token.
  const hatchBackground =
    `repeating-linear-gradient(45deg, transparent 0 14px, rgba(105,67,255,0.04) 14px 28px), ${t.surface2}`;

  return (
    <div
      style={{
        background: t.surface,
        border: `1px solid ${t.line}`,
        borderRadius: 16, // prototype --r-card-lg (between token lg=14 and xl=20)
        boxShadow:
          '0 1px 2px rgba(26,21,53,0.04), 0 6px 18px rgba(26,21,53,0.05)',
        overflow: 'hidden',
      }}
    >
      {/* ── Media slot: real sign image OR honest dashed placeholder ───────── */}
      {showRealImage && (
        <div
          style={{
            height: 140,
            background: hatchBackground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${t.line}`,
          }}
        >
          <img
            src={`/${imageRef}`}
            alt={imageAlt}
            style={{
              maxHeight: 116,
              maxWidth: '88%',
              height: 'auto',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
            decoding="async"
            loading="lazy"
            onError={() => setBroken(true)}
          />
        </div>
      )}

      {showPlaceholder && (
        <div
          role="img"
          aria-label={placeholderCaption}
          style={{
            // honest dashed placeholder: dashed top/sides (the `.imgslot` cue)
            // with a solid hairline bottom that joins it to the body, since the
            // slot spans the full card width inside the rounded `overflow:hidden`.
            minHeight: 140,
            background: t.surface2,
            borderTop: `1.5px dashed ${t.lineStrong}`,
            borderLeft: `1.5px dashed ${t.lineStrong}`,
            borderRight: `1.5px dashed ${t.lineStrong}`,
            borderBottom: `1px solid ${t.line}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: 16,
            color: t.textFaint,
            textAlign: 'center',
          }}
        >
          <MediaGlyph size={34} color={t.textFaint} />
          <span
            style={{
              fontFamily: t.fonts.body,
              fontSize: 12.5,
              fontWeight: 500,
              letterSpacing: 0.1,
            }}
          >
            {placeholderCaption}
          </span>
        </div>
      )}

      {/* ── Body: question text + children (e.g. VeroFalsoPills) ───────────── */}
      <div
        style={{
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: t.fonts.body,
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.4,
            color: t.text,
            letterSpacing: -0.1,
          }}
        >
          {text}
        </p>
        {children}
      </div>
    </div>
  );
}
